import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { base_url } from '../baseUrl';

/**
 * useRoles
 * 
 * Hook for managing roles via API endpoints:
 * - GET /api/roles - Fetch all roles
 * - POST /api/roles - Create a new role
 * - PUT /api/roles/:id - Update an existing role
 */

const defaultPermissions = [
  // Action Permissions
  { key: "canCreateSalesOrders", label: "Create sales orders" },
  { key: "canViewPrices", label: "View prices" },
  // Module Access Permissions
  { key: "canViewDashboard", label: "Dashboard" },
  { key: "canViewItems", label: "Items" },
  { key: "canViewAllOrders", label: "All Orders" },
  { key: "canViewOrderedItems", label: "View Ordered Items" },
  { key: "canViewMyAccount", label: "My Account" },
  { key: "canManageUsers", label: "User Management" },
  { key: "canManageRolesAndPermissions", label: "Roles and Permissions" },
  { key: "canManageItems", label: "Manage Items" },
  { key: "canViewAllUsers", label: "All Users" },
];

// Mapping between API permission keys and component permission keys
const API_TO_COMPONENT_KEY_MAP = {
  createSalesOrders: "canCreateSalesOrders",
  viewPrices: "canViewPrices",
  dashboard: "canViewDashboard",
  items: "canViewItems",
  allOrders: "canViewAllOrders",
  viewOrderedItems: "canViewOrderedItems",
  myAccount: "canViewMyAccount",
  userManagement: "canManageUsers",
  rolesAndPermissions: "canManageRolesAndPermissions",
  manageItems: "canManageItems",
  allUsers: "canViewAllUsers",
};

// Reverse mapping: component keys to API keys
const COMPONENT_TO_API_KEY_MAP = Object.fromEntries(
  Object.entries(API_TO_COMPONENT_KEY_MAP).map(([apiKey, componentKey]) => [
    componentKey,
    apiKey,
  ])
);

/**
 * Transform API permissions format to component format
 * API: { createSalesOrders: true, dashboard: true }
 * Component: { canCreateSalesOrders: true, canViewDashboard: true }
 */
const transformApiPermissionsToComponent = (apiPermissions) => {
  if (!apiPermissions || typeof apiPermissions !== "object") {
    return {};
  }
  
  const componentPermissions = {};
  Object.entries(apiPermissions).forEach(([apiKey, value]) => {
    const componentKey = API_TO_COMPONENT_KEY_MAP[apiKey];
    if (componentKey) {
      componentPermissions[componentKey] = value;
    } else {
      // If key not in map, use it as-is (backward compatibility)
      componentPermissions[apiKey] = value;
    }
  });
  
  return componentPermissions;
};

/**
 * Transform component permissions format to API format
 * Component: { canCreateSalesOrders: true, canViewDashboard: true }
 * API: { createSalesOrders: true, dashboard: true }
 */
const transformComponentPermissionsToApi = (componentPermissions) => {
  if (!componentPermissions || typeof componentPermissions !== "object") {
    return {};
  }
  
  const apiPermissions = {};
  Object.entries(componentPermissions).forEach(([componentKey, value]) => {
    const apiKey = COMPONENT_TO_API_KEY_MAP[componentKey];
    if (apiKey) {
      apiPermissions[apiKey] = value;
    } else {
      // If key not in map, use it as-is (backward compatibility)
      apiPermissions[componentKey] = value;
    }
  });
  
  return apiPermissions;
};

const useRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Get authentication token from localStorage
  const getHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = storedUser ? JSON.parse(storedUser).token : null;
    
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  };

  // Fetch all roles
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${base_url}api/roles`, getHeaders());
      const rolesData = data?.roles || data || [];
      
      // Transform permissions from API format to component format
      const transformedRoles = rolesData.map((role) => ({
        ...role,
        permissions: transformApiPermissionsToComponent(role.permissions || {}),
      }));
      
      setRoles(transformedRoles);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new role
  const createRole = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      // Transform permissions from component format to API format
      const apiPayload = {
        ...payload,
        permissions: payload.permissions
          ? transformComponentPermissionsToApi(payload.permissions)
          : undefined,
      };
      
      const { data } = await axios.post(
        `${base_url}api/roles`,
        apiPayload,
        getHeaders()
      );
      
      const newRole = data?.role || data;
      
      // Transform permissions back to component format for local state
      const transformedRole = {
        ...newRole,
        permissions: transformApiPermissionsToComponent(newRole.permissions || {}),
      };
      
      setRoles((prev) => [...prev, transformedRole]);
      setSaving(false);
      return transformedRole;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create role');
      setSaving(false);
      return null;
    }
  };

  // Update an existing role
  const updateRole = async (id, updates) => {
    setSaving(true);
    setError(null);
    try {
      // Transform permissions from component format to API format
      const apiUpdates = {
        ...updates,
        permissions: updates.permissions
          ? transformComponentPermissionsToApi(updates.permissions)
          : undefined,
      };
      
      const { data } = await axios.put(
        `${base_url}api/roles/${id}`,
        apiUpdates,
        getHeaders()
      );
      
      const updatedRole = data?.role || data;
      
      // Transform permissions back to component format for local state
      const transformedRole = {
        ...updatedRole,
        permissions: transformApiPermissionsToComponent(updatedRole.permissions || {}),
      };
      
      setRoles((prev) =>
        prev.map((r) => (r._id === id ? transformedRole : r))
      );
      setSaving(false);
      return transformedRole;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update role');
      setSaving(false);
      return null;
    }
  };

  // Delete a role (if needed in the future)
  const deleteRole = async (id) => {
    setSaving(true);
    setError(null);
    try {
      await axios.delete(`${base_url}api/roles/${id}`, getHeaders());
      setRoles((prev) => prev.filter((r) => r._id !== id));
      setSaving(false);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete role');
      setSaving(false);
      return false;
    }
  };

  // Fetch roles on mount
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    roles,
    loading,
    saving,
    error,
    permissionsCatalog: defaultPermissions,
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
  };
};

export default useRoles;
